import React, { useEffect, useState } from "react";
import usersService from "../../../services/UsersService";
import SwitchCom from "../../Helpers/SwitchCom";

// Same shape/copy/behavior as myfit-app's EventRemindersScreen:
// a master on/off switch, plus a lead-time list that's disabled
// (not hidden) while reminders are off.
const LEAD_TIMES = [
  { minutes: 0, label: "At time of event" },
  { minutes: 5, label: "5 min before" },
  { minutes: 15, label: "15 min before" },
  { minutes: 30, label: "30 min before" },
  { minutes: 60, label: "1 hour before" },
];

const DEFAULTS = { enabled: true, lead_minutes: 15 };

export default function EventRemindersTab() {
  const api = new usersService();

  const [settings, setSettings] = useState(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);
  const [savingEnabled, setSavingEnabled] = useState(false);
  const [savingLeadMinutes, setSavingLeadMinutes] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .fetchEventReminderSettings()
      .then((res) => setSettings(res.data))
      .catch(() => setError("Could not load settings."))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onToggleEnabled = async () => {
    const previous = settings;
    const enabled = !settings.enabled;
    setSettings((s) => ({ ...s, enabled }));
    setSavingEnabled(true);
    setError(null);
    try {
      await api.updateEventReminderSettings({ enabled });
    } catch (err) {
      setSettings(previous);
      setError("Could not save. Try again.");
    } finally {
      setSavingEnabled(false);
    }
  };

  const onSelectLeadMinutes = async (leadMinutes) => {
    if (!settings.enabled || leadMinutes === settings.lead_minutes) return;
    const previous = settings;
    setSettings((s) => ({ ...s, lead_minutes: leadMinutes }));
    setSavingLeadMinutes(leadMinutes);
    setError(null);
    try {
      await api.updateEventReminderSettings({ lead_minutes: leadMinutes });
    } catch (err) {
      setSettings(previous);
      setError("Could not save. Try again.");
    } finally {
      setSavingLeadMinutes(null);
    }
  };

  if (isLoading) {
    return (
      <div className="event-reminders-tab w-full">
        <p className="table-empty">Loading…</p>
      </div>
    );
  }

  return (
    <div className="event-reminders-tab w-full">
      {error && <p className="text-red-700 mb-4">{error}</p>}

      <div className="flex justify-between items-center mb-4 pb-4 border-b dark:border-[#5356fb29] border-light-purple">
        <div>
          <p className="text-xl tracking-wide text-dark-gray dark:text-white font-bold">
            Event reminders
          </p>
          <p className="text-base tracking-wide text-thin-light-gray">
            Get notified before your scheduled events — medication, appointments, and check-ups.
          </p>
        </div>
        {savingEnabled ? (
          <span className="text-sm text-thin-light-gray shrink-0 ml-4">Saving…</span>
        ) : (
          <SwitchCom value={settings.enabled} handler={onToggleEnabled} />
        )}
      </div>

      <h2
        className={`text-sm tracking-wide uppercase font-bold mb-3 ${
          settings.enabled ? "text-thin-light-gray" : "text-thin-light-gray opacity-50"
        }`}
      >
        Remind me
      </h2>
      <ul className={`w-full ${settings.enabled ? "" : "opacity-50"}`}>
        {LEAD_TIMES.map((option, i) => (
          <li
            key={option.minutes}
            className={`flex justify-between items-center py-3.5 ${
              i === LEAD_TIMES.length - 1 ? "" : "border-b dark:border-[#5356fb29] border-light-purple"
            }`}
          >
            <button
              type="button"
              className="text-base text-dark-gray dark:text-white text-left flex-1"
              onClick={() => onSelectLeadMinutes(option.minutes)}
              disabled={!settings.enabled}
            >
              {option.label}
            </button>
            {savingLeadMinutes === option.minutes ? (
              <span className="text-sm text-thin-light-gray">Saving…</span>
            ) : (
              settings.lead_minutes === option.minutes && (
                <span className="text-purple font-bold">&#10003;</span>
              )
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
